import { useState, useCallback } from 'react';
import { SimulationResult, ForecastType } from './types';

export const useMonteCarloSimulation = () => {
  const [isCalculating, setIsCalculating] = useState(false);

  const parseHistoricalData = useCallback((data: string): number[] => {
    return data
      .split(',')
      .map(item => parseFloat(item.trim()))
      .filter(item => !isNaN(item) && item > 0);
  }, []);

  const generateRandomValue = useCallback((historicalData: number[]): number => {
    // Bootstrap sampling with some noise for better variability
    const randomIndex = Math.floor(Math.random() * historicalData.length);
    const baseValue = historicalData[randomIndex];
    
    // Add small random variation (±10%) to increase variability
    const variation = (Math.random() - 0.5) * 0.2; // -10% to +10%
    const variedValue = baseValue * (1 + variation);
    
    // Ensure we don't get negative values
    return Math.max(0.1, variedValue);
  }, []);

  const runMonteCarloSimulation = useCallback((
    historicalData: number[], 
    targetSprints: number, 
    targetItems: number, 
    forecastType: ForecastType
  ): SimulationResult => {
    const simulations = 10000;
    const results: number[] = [];

    for (let i = 0; i < simulations; i++) {
      if (forecastType === 'items-in-sprints') {
        // Simulate how many items completed in X sprints
        let totalCompleted = 0;
        for (let sprint = 0; sprint < targetSprints; sprint++) {
          totalCompleted += generateRandomValue(historicalData);
        }
        results.push(totalCompleted);
      } else {
        // Simulate how many sprints needed to complete X items
        let totalCompleted = 0;
        let sprintsNeeded = 0;
        while (totalCompleted < targetItems && sprintsNeeded < 50) { // Cap at 50 sprints
          totalCompleted += generateRandomValue(historicalData);
          sprintsNeeded++;
        }
        results.push(sprintsNeeded);
      }
    }

    // Sort results for percentile calculation
    results.sort((a, b) => a - b);

    const percentiles = {
      p50: results[Math.floor(results.length * 0.5)],
      p80: forecastType === 'items-in-sprints' 
        ? results[Math.floor(results.length * 0.2)] // 80% confidence = 20th percentile (minimum items)
        : results[Math.floor(results.length * 0.8)], // 80% confidence = 80th percentile (maximum sprints)
      p90: forecastType === 'items-in-sprints'
        ? results[Math.floor(results.length * 0.1)]  // 90% confidence = 10th percentile (minimum items)
        : results[Math.floor(results.length * 0.9)], // 90% confidence = 90th percentile (maximum sprints)
      p99: forecastType === 'items-in-sprints'
        ? results[Math.floor(results.length * 0.01)] // 99% confidence = 1st percentile (minimum items)
        : results[Math.floor(results.length * 0.99)] // 99% confidence = 99th percentile (maximum sprints)
    };

    return {
      percentiles,
      simulations
    };
  }, [generateRandomValue]);

  const runSimulation = useCallback(async (
    throughputData: string,
    targetSprints: number,
    targetItems: number,
    forecastType: ForecastType
  ): Promise<SimulationResult> => {
    if (!throughputData.trim()) {
      throw new Error('Please enter historical data');
    }

    const historicalData = parseHistoricalData(throughputData);
    if (historicalData.length < 2) {
      throw new Error('Please enter at least 2 data points');
    }

    if (isNaN(targetSprints) || isNaN(targetItems) || targetSprints <= 0 || targetItems <= 0) {
      throw new Error('Please enter valid numbers');
    }

    setIsCalculating(true);
    
    // Simulate async calculation
    return new Promise((resolve) => {
      setTimeout(() => {
        const result = runMonteCarloSimulation(historicalData, targetSprints, targetItems, forecastType);
        setIsCalculating(false);
        resolve(result);
      }, 500);
    });
  }, [parseHistoricalData, runMonteCarloSimulation]);

  return {
    isCalculating,
    runSimulation
  };
};
